import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { useAuth } from '../contexts/AuthContext';
import { PartnerProvider } from '../contexts/PartnerContext';
import { PartnerScopeProvider } from '../contexts/PartnerScopeContext';
import { PartnerSwitcher } from './PartnerSwitcher';

const USER_AVATAR_URL =
  'https://cdn2.sophia.org/static/user_image/relaunch_missing-ddf3e4cf2ad648228829b6db06bb131122364a5c3ec0086dcdfd8cf5f2333501.svg';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  {
    label: 'Transcripts',
    path: '/transcripts',
    requiredRoles: ['transcript_viewer', 'sophia_partner_admin'],
  },
  {
    label: 'Students',
    path: '/students',
    requiredRoles: ['academic_advisor', 'sophia_partner_admin'],
  },
  { label: 'Partners', path: '/partners', requiredRoles: ['sophia_partner_admin'] },
  { label: 'API Keys', path: '/api-keys', requiredRoles: ['api_developer'] },
  { label: 'API Docs', path: '/api-docs', requiredRoles: ['api_developer'] },
  { label: 'Users', path: '/users', requiredRoles: ['sophia_admin'] },
  {
    label: 'Manage Users',
    path: '/manage-users',
    requiredRoles: ['client_admin', 'client_user_management'],
  },
];

const TAB_PADDING = 20;

export function AppLayout(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, roles, signOut } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleSignOut = async () => {
    setAnchorEl(null);
    await signOut();
    navigate('/login');
  };

  const visibleNavItems = React.useMemo(() => {
    const isAdmin = roles.includes('sophia_admin');
    return NAV_ITEMS.filter((item) => {
      if (!item.requiredRoles || item.requiredRoles.length === 0) return true;
      if (isAdmin) return true;
      return item.requiredRoles.some((role) => roles.includes(role));
    });
  }, [roles]);

  const activeIndex = Math.max(
    0,
    visibleNavItems.findIndex((item) => location.pathname.startsWith(item.path))
  );

  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const el = tabRefs.current[activeIndex];
    if (el) {
      setIndicatorStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeIndex]);

  return (
    <PartnerScopeProvider>
      <PartnerProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            bgcolor: 'background.default',
          }}
        >
          <AppBar position="static" sx={{ position: 'relative' }}>
            <Container maxWidth="xl">
              <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                <Box
                  component="img"
                  src="/sophia-logo.svg"
                  alt="Sophia Learning"
                  onClick={() => navigate('/dashboard')}
                  sx={{ width: 137, mr: 2, cursor: 'pointer' }}
                />
                <Box
                  component="span"
                  sx={{
                    ml: '10px',
                    backgroundColor: 'transparent',
                    border: '1px solid #6D6E71',
                    borderRadius: '4px',
                    fontSize: '13px',
                    padding: '3px 8px',
                    letterSpacing: '1px',
                    color: '#6D6E71',
                    fontWeight: 400,
                  }}
                >
                  PARTNER PORTAL
                </Box>
                <Box sx={{ ml: 'auto', mr: 8 }}>
                  <PartnerSwitcher />
                </Box>
              </Box>
              {/* Navigation tabs */}
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex' }}>
                  {visibleNavItems.map((item, i) => {
                    const isActive = i === activeIndex;
                    return (
                      <Box
                        key={item.path}
                        ref={(el: HTMLDivElement | null) => {
                          tabRefs.current[i] = el;
                        }}
                        onClick={() => navigate(item.path)}
                        sx={{
                          px: `${TAB_PADDING}px`,
                          pt: 0.5,
                          pb: 0.75,
                          cursor: 'pointer',
                          '&:hover .nav-label': {
                            color: '#250e62',
                          },
                        }}
                      >
                        <Typography
                          className="nav-label"
                          sx={{
                            fontSize: 16,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? '#250e62' : '#6D6E71',
                            transition: 'color 0.3s ease',
                            userSelect: 'none',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {/* Sliding underline indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                    height: 2,
                    bgcolor: '#250e62',
                    transition:
                      'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </Box>
            </Container>
            {user && (
              <Container
                maxWidth="xl"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#D0DF00',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                  }}
                >
                  <Box
                    component="img"
                    src={USER_AVATAR_URL}
                    alt="User"
                    sx={{ width: 40, height: 40 }}
                  />
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={() => setAnchorEl(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#250e62' }}>
                      {user.displayName || user.email?.split('@')[0]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6D6E71' }}>
                      {user.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem
                    onClick={handleSignOut}
                    sx={{
                      py: 1.5,
                      fontSize: '0.875rem',
                      gap: 1.5,
                      transition: 'background-color 0.3s ease',
                      '&:hover': { bgcolor: '#EFF3F5' },
                    }}
                  >
                    <i
                      className="fa-thin fa-right-from-bracket"
                      style={{ fontSize: 16, color: '#6D6E71' }}
                    />
                    Sign out
                  </MenuItem>
                </Menu>
              </Container>
            )}
          </AppBar>

          <Box
            key={location.pathname}
            component="main"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              animation: 'fadeIn 0.3s ease-in-out',
              '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            <Outlet />
          </Box>

          <Box
            component="footer"
            sx={{
              py: 2,
              px: 3,
              textAlign: 'center',
              bgcolor: '#444',
            }}
          >
            <Typography variant="body2" sx={{ color: '#fff' }}>
              Sophia Learning
            </Typography>
          </Box>
        </Box>
      </PartnerProvider>
    </PartnerScopeProvider>
  );
}
